import React, { useState } from 'react';
import { OptimizationResult, AllocationStrategy } from '../../types';
import AllocationVisualization from './AllocationVisualization';
import './OptimizationResults.css';

interface OptimizationResultsProps {
  result: OptimizationResult;
  nodeNameMap: Map<string, string>;
  onClear: () => void;
}

const OptimizationResults: React.FC<OptimizationResultsProps> = ({ result, nodeNameMap, onClear }) => {
  const [showLPDetails, setShowLPDetails] = useState(false);
  const [activeTab, setActiveTab] = useState<'formulation' | 'solution'>('formulation');

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(Math.round(num * 100) / 100);
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 1) {
      return `${Math.round(seconds * 1000)}ms`;
    }
    return `${seconds.toFixed(2)}s`;
  };

  const getNodeDisplayName = (nodeId: string): string => {
    const nodeName = nodeNameMap.get(nodeId);
    return nodeName || nodeId; // Fallback to nodeId if name not found
  };

  const isAdvancedOptimization = result.solver_used === 'cvxpy' && 
    (result as any).lp_formulation && 
    (result as any).solution_details;

  const exportToCSV = () => {
    const headers = [
      'Node ID',
      'Node Name',
      'Demand',
      'On Hand Inventory',
      'Allocation',
      'After Inventory',
      'Demand Gap Reduction'
    ];
    
    const csvContent = [
      headers.join(','),
      ...result.allocations.map(allocation => [
        allocation.node_id,
        getNodeDisplayName(allocation.node_id),
        allocation.demand,
        allocation.before_inventory,
        allocation.allocated_quantity,
        allocation.after_inventory,
        allocation.demand_gap_reduction
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `optimization_results_${result.optimization_id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToJSON = () => {
    const exportData = {
      optimization_id: result.optimization_id,
      timestamp: result.timestamp,
      status: result.status,
      total_demand_gap_reduction: result.total_demand_gap_reduction,
      solve_time_seconds: result.solve_time_seconds,
      strategy: result.input_data.allocation_strategy,
      purchase_order_quantity: result.input_data.purchase_quantity,
      allocations: result.allocations,
      ...(isAdvancedOptimization && {
        lp_formulation: (result as any).lp_formulation,
        solution_details: (result as any).solution_details
      })
    };

    const jsonContent = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `optimization_results_${result.optimization_id}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportLPFormulation = () => {
    if (!isAdvancedOptimization) return;
    
    const lpData = (result as any).lp_formulation;
    const solutionData = (result as any).solution_details;
    
    const lpContent = `# Linear Programming Formulation
# Generated: ${new Date(result.timestamp).toLocaleString()}
# Optimization ID: ${result.optimization_id}

## Problem Type: ${lpData.problem_type}
## Solver: ${lpData.solver}

## Variables:
${Object.entries(lpData.variables).map(([key, value]: [string, any]) => 
  `# ${value.description}\n${Object.entries(value.nodes).map(([varName, desc]) => `${varName}: ${desc}`).join('\n')}`
).join('\n\n')}

## Objective Function:
# ${lpData.objective.description}
${lpData.objective.type} ${lpData.objective.mathematical_form}

## Constraints:
${Object.entries(lpData.constraints).map(([key, constraint]: [string, any]) => 
  `# ${constraint.description}\n${constraint.mathematical_form}`
).join('\n\n')}

## Problem Data:
${Object.entries(lpData.problem_data).map(([key, value]) => 
  `${key}: ${typeof value === 'object' ? JSON.stringify(value, null, 2) : value}`
).join('\n')}

## Solution:
# Status: ${solutionData.solution_status}
# Objective Value: ${solutionData.quality_metrics.objective_value}
# Total Gap Remaining: ${solutionData.quality_metrics.total_gap_remaining}

## Variable Values:
${Object.entries(solutionData.solution_values.allocation_variables).map(([varName, data]: [string, any]) => 
  `${varName} = ${data.value} (${data.description})`
).join('\n')}

${Object.entries(solutionData.solution_values.gap_variables).map(([varName, data]: [string, any]) => 
  `${varName} = ${data.value} (${data.description})`
).join('\n')}
`;

    const blob = new Blob([lpContent], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `lp_formulation_${result.optimization_id}.txt`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalAllocated = result.allocations.reduce((sum, allocation) => sum + allocation.allocated_quantity, 0);

  return (
    <div className="optimization-results">
      <div className="results-header">
        <h3>Optimization Results</h3>
        <div className="results-summary">
          <div className="summary-item">
            <span className="summary-label">Status:</span>
            <span className={`summary-value status-${result.status}`}>
              {result.status.toUpperCase()}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Solve Time:</span>
            <span className="summary-value">{formatTime(result.solve_time_seconds)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Strategy:</span>
            <span className="summary-value">
              {result.input_data.allocation_strategy === AllocationStrategy.SPLIT ? 'Split' : 'Intact'}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Solver:</span>
            <span className="summary-value">
              {result.solver_used === 'cvxpy' ? 'Advanced (LP)' : 'Simple'}
            </span>
          </div>
        </div>
      </div>

      <div className="results-metrics">
        <div className="metric-card">
          <h4>Total Demand Gap Reduction</h4>
          <div className="metric-value primary">
            {formatNumber(result.total_demand_gap_reduction)} units
          </div>
          <p className="metric-description">
            Total reduction in unmet demand across all warehouse nodes
          </p>
        </div>

        <div className="metric-card">
          <h4>Purchase Order Quantity</h4>
          <div className="metric-value">
            {formatNumber(result.input_data.purchase_quantity)} units
          </div>
          <p className="metric-description">
            Total quantity allocated: {formatNumber(totalAllocated)} units
          </p>
        </div>
      </div>

      {isAdvancedOptimization && (
        <div className="lp-details-section">
          <div className="lp-header">
            <h4>Linear Programming Details</h4>
            <button
              className="btn btn-secondary"
              onClick={() => setShowLPDetails(!showLPDetails)}
            >
              {showLPDetails ? 'Hide LP Details' : 'Show LP Details'}
            </button>
          </div>
          
          {showLPDetails && (
            <div className="lp-content">
              <div className="lp-tabs">
                <button
                  className={`tab-button ${activeTab === 'formulation' ? 'active' : ''}`}
                  onClick={() => setActiveTab('formulation')}
                >
                  LP Formulation
                </button>
                <button
                  className={`tab-button ${activeTab === 'solution' ? 'active' : ''}`}
                  onClick={() => setActiveTab('solution')}
                >
                  Solution Details
                </button>
              </div>

              {activeTab === 'formulation' && (
                <div className="lp-formulation">
                  <div className="formulation-section">
                    <h5>Problem Type</h5>
                    <p>{(result as any).lp_formulation.problem_type}</p>
                    <p><strong>Solver:</strong> {(result as any).lp_formulation.solver}</p>
                  </div>

                  <div className="formulation-section">
                    <h5>Decision Variables</h5>
                    {Object.entries((result as any).lp_formulation.variables).map(([_, variable]: [string, any]) => (
                      <div key={variable.description} className="variable-group">
                        <h6>{variable.description}</h6>
                        <p><strong>Count:</strong> {variable.count}</p>
                        <p><strong>Domain:</strong> {variable.domain}</p>
                      </div>
                    ))}
                  </div>

                  <div className="formulation-section">
                    <h5>Objective Function</h5>
                    <p><strong>Type:</strong> {(result as any).lp_formulation.objective.type}</p>
                    <p><strong>Function:</strong> {(result as any).lp_formulation.objective.mathematical_form}</p>
                    <p>{(result as any).lp_formulation.objective.description}</p>
                  </div>

                  <div className="formulation-section">
                    <h5>Constraints</h5>
                    {Object.entries((result as any).lp_formulation.constraints).map(([_, constraint]: [string, any]) => (
                      <div key={constraint.description} className="constraint-group">
                        <h6>{constraint.description}</h6>
                        <p><strong>Type:</strong> {constraint.type}</p>
                        <code>{constraint.mathematical_form}</code>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'solution' && (
                <div className="solution-details">
                  <div className="solution-section">
                    <h5>Solution Status</h5>
                    <p><strong>Status:</strong> {(result as any).solution_details.solution_status}</p>
                    <p><strong>Objective Value:</strong> {formatNumber((result as any).solution_details.quality_metrics.objective_value)}</p>
                    <p><strong>Total Gap Remaining:</strong> {formatNumber((result as any).solution_details.quality_metrics.total_gap_remaining)}</p>
                  </div>

                  <div className="solution-section">
                    <h5>Variable Values</h5>
                    <div className="variable-values">
                      <h6>Allocation Variables</h6>
                      {Object.entries((result as any).solution_details.solution_values.allocation_variables).map(([varName, data]: [string, any]) => (
                        <div key={varName} className="variable-value">
                          <code>{varName}</code> = {formatNumber(data.value)} ({data.description})
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="solution-section">
                    <h5>Quality Metrics</h5>
                    <div className="quality-metrics">
                      <p><strong>Nodes with Allocations:</strong> {(result as any).solution_details.quality_metrics.nodes_with_allocations}</p>
                      <p><strong>Nodes with Remaining Gaps:</strong> {(result as any).solution_details.quality_metrics.nodes_with_remaining_gaps}</p>
                      <p><strong>Demand Satisfaction Rate:</strong> {((result as any).solution_details.quality_metrics.allocation_efficiency.demand_satisfaction_rate * 100).toFixed(1)}%</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <AllocationVisualization 
        allocations={result.allocations} 
        nodeNameMap={nodeNameMap}
      />

      <div className="allocation-details">
        <h4>Allocation Details</h4>
        <div className="allocation-table" data-version="v2.0">
          <div className="table-header" role="row">
            <div className="col-node" role="columnheader">Node</div>
            <div className="col-demand" role="columnheader">Demand</div>
            <div className="col-onhand" role="columnheader">On Hand</div>
            <div className="col-allocation" role="columnheader">Allocation</div>
            <div className="col-after" role="columnheader">After</div>
            <div className="col-reduction" role="columnheader">Gap Reduction</div>
          </div>
          
          {result.allocations
            .sort((a, b) => {
              const nameA = getNodeDisplayName(a.node_id).toLowerCase();
              const nameB = getNodeDisplayName(b.node_id).toLowerCase();
              return nameA.localeCompare(nameB);
            })
            .map((allocation) => (
            <div 
              key={allocation.node_id} 
              className={`table-row ${allocation.allocated_quantity > 0 ? 'receiving-inventory' : ''}`}
              role="row"
              data-node-id={allocation.node_id}
            >
              <div className="col-node" role="gridcell">
                <div className="node-info">
                  <span className="node-name">{getNodeDisplayName(allocation.node_id)}</span>
                </div>
                {allocation.allocated_quantity > 0 && (
                  <span className="receiving-indicator">📦</span>
                )}
              </div>
              <div className="col-demand" role="gridcell">
                {formatNumber(allocation.demand)}
              </div>
              <div className="col-onhand" role="gridcell">
                {formatNumber(allocation.before_inventory)}
              </div>
              <div className="col-allocation" role="gridcell">
                <span className={allocation.allocated_quantity > 0 ? 'allocated-quantity' : 'no-allocation'}>
                  {formatNumber(allocation.allocated_quantity)}
                </span>
              </div>
              <div className="col-after" role="gridcell">
                {formatNumber(allocation.after_inventory)}
              </div>
              <div className="col-reduction" role="gridcell">
                <span className={allocation.demand_gap_reduction > 0 ? 'positive-reduction' : 'no-reduction'}>
                  {allocation.demand_gap_reduction > 0 ? '+' : ''}{formatNumber(allocation.demand_gap_reduction)}
                </span>
              </div>
            </div>
          ))}
          
          {/* Totals Row */}
          <div className="table-row totals-row" role="row">
            <div className="col-node" role="gridcell">
              <div className="node-info">
                <span className="node-name totals-label">TOTAL</span>
              </div>
            </div>
            <div className="col-demand" role="gridcell">
              {formatNumber(result.allocations.reduce((sum, allocation) => sum + allocation.demand, 0))}
            </div>
            <div className="col-onhand" role="gridcell">
              {formatNumber(result.allocations.reduce((sum, allocation) => sum + allocation.before_inventory, 0))}
            </div>
            <div className="col-allocation" role="gridcell">
              <span className="allocated-quantity">
                {formatNumber(totalAllocated)}
              </span>
            </div>
            <div className="col-after" role="gridcell">
              {formatNumber(result.allocations.reduce((sum, allocation) => sum + allocation.after_inventory, 0))}
            </div>
            <div className="col-reduction" role="gridcell">
              <span className="positive-reduction">
                +{formatNumber(result.total_demand_gap_reduction)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="results-actions">
        <button
          className="btn btn-secondary"
          onClick={onClear}
        >
          Clear Results
        </button>
        
        <button
          className="btn btn-primary"
          onClick={exportToCSV}
          title="Export allocation results as CSV file"
        >
          Export CSV
        </button>

        <button
          className="btn btn-primary"
          onClick={exportToJSON}
          title="Export complete optimization results as JSON file"
        >
          Export JSON
        </button>

        {isAdvancedOptimization && (
          <button
            className="btn btn-primary"
            onClick={exportLPFormulation}
            title="Export LP formulation and solution as text file"
          >
            Export LP File
          </button>
        )}
      </div>

      <div className="results-footer">
        <p className="optimization-id">
          Optimization ID: <code>{result.optimization_id}</code>
        </p>
        <p className="timestamp">
          Generated: {new Date(result.timestamp).toLocaleString()}
        </p>
      </div>
    </div>
  );
};

export default OptimizationResults;